import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { RowWithGap, RowWithoutGap } from '@components/styles';
import { MenuItem } from '@components/ringil3/types';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgButtonTagEdit from '@components/framework/tag/ButtonTagEdit.tsx';
import SvgCoStar from '@icons/CoStar.tsx';
import SecuredArea from '@components/security/SecuredArea';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import { SecuredConfig } from '@components/security/securedDecoration';
import { clickableTextClass } from '@components/RingilComponents/clickableText.ts';

interface ComponentProps {
  title: string;
  /** Makes the title a link, e.g. to the full page of a detail shown inside another page. */
  titleLink?: string;
  subtitle?: string;
  buttons?: React.ReactNode[];
  moreActions?: MenuItem[];
  labels?: React.ReactNode;
  headingVariant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  onEdit?: (e: React.MouseEvent) => void;
  editSecured?: SecuredConfig;
  isFavorite?: boolean;
  className?: string;
  noGap?: boolean;
}

const Heading: React.FC<ComponentProps> = ({
  title,
  titleLink,
  subtitle,
  buttons,
  moreActions,
  labels,
  headingVariant = 'h1',
  onEdit,
  editSecured,
  isFavorite = false,
  noGap = false,
  className,
}) => {
  // Anchor target for popovers must be an HTML element with a layout box; a raw <svg> has no
  // offsetWidth/offsetParent and breaks PrimeReact overlay positioning, so wrap it in a span.
  const editButton = onEdit && (
    <EditButtonWrapper onClick={onEdit}>
      <EditButton />
    </EditButtonWrapper>
  );
  const Content = () => (
    <>
      <LeftAlignedRow>
        {/* The title keeps its natural width (`shrink-0`) so the identifier is never nibbled at while
            the name next to it still has room to give up — flex shrinking took ~1px off it otherwise,
            which is enough for text-overflow to eat the last characters. `max-w-full` is what still
            truncates a title that is long all by itself, without letting it push the buttons out. */}
        <COHeadline
          variant={headingVariant}
          title={
            titleLink ? (
              <Link to={titleLink} className={`${clickableTextClass} min-w-0`}>
                <EllipsisText className="min-w-0">{title}</EllipsisText>
              </Link>
            ) : (
              <EllipsisText className="min-w-0">{title}</EllipsisText>
            )
          }
          className="min-w-0 max-w-full shrink-0"
        ></COHeadline>
        {subtitle && (
          <>
            <Separator>|</Separator>
            <Subtitle>{subtitle}</Subtitle>
          </>
        )}
      </LeftAlignedRow>
      {editButton &&
        (editSecured ? (
          <SecuredArea id={editSecured.id} label={editSecured.label} visible={editSecured.visible} editable={editSecured.editable}>
            {editButton}
          </SecuredArea>
        ) : (
          editButton
        ))}
      {isFavorite && <SvgCoStar className={'text-yellow-400 fill-current'} />}
      {labels && <>{labels}</>}
    </>
  );

  return (
    <RingilRowWithButtons className={className}>
      {noGap ? <RowWithoutGap className="min-w-0">{Content()}</RowWithoutGap> : <RowWithGap className="min-w-0">{Content()}</RowWithGap>}
      {buttons?.length ? (
        // shrink-0: the buttons are the fixed part of this row. Without it a long title or name grows
        // the left-hand side until the buttons are pushed past the edge of the screen.
        <div className="flex shrink-0 flex-row items-center gap-2">
          {buttons.map((child, idx) => {
            return <span key={idx}>{child}</span>;
          })}
        </div>
      ) : null}
    </RingilRowWithButtons>
  );
};

const RingilRowWithButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

// styled(EllipsisText) rather than a styled.span: the name is free text and one long word used to
// push everything to its right off the screen. It now ends in an ellipsis, with the full name on
// hover. Typography is unchanged.
const Subtitle = styled(EllipsisText)`
  min-width: 0;
  /* The name is what gives way when the row runs out of room, so it shrinks far more readily than
     anything beside it. Weighted here rather than by lowering the title's factor: a flex line whose
     factors sum to less than one only takes back that fraction of the overflow, which left a long
     title overflowing instead of truncating. */
  flex-shrink: 100;
  color: #979797;
  font-family: 'DM Sans', sans-serif;
  font-size: 26px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.52px;
`;

const Separator = styled.span`
  color: #979797;
  margin: 0 0.5rem;
  flex-shrink: 0;
`;

const LeftAlignedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  /* Both the title and the name truncate, and neither can do that inside a flex item that refuses to
     shrink below its content (min-width defaults to auto). */
  min-width: 0;
`;

const EditButtonWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const EditButton = styled(SvgButtonTagEdit)`
  cursor: pointer;
`;

export default Heading;
